"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRainWebSocket } from "./useRainWebSocket";
import { RainDrops, RainDropsHandle } from "./RainDrops";
import { RainEndSequence } from "./RainEndSequence";
import { FrozenDrop } from "./RainEndPhase";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Timer, UserRound, Users } from "lucide-react";

const CURRENCY_UNIT = "ESPONT";
const CONTAINER_ANIMATION_DURATION = 0.5;
const EXIT_ANIMATION_DURATION = 0.85;

const EspontIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <img src="/espont.png" alt="" className={`${className} object-contain`} />
);

interface RainOverlayProps {
  enabled?: boolean;
  onEventReceived?: (event: any) => void;
  animationSpeed?: number;
  autoFadeOut?: boolean;
  fadeOutDelay?: number;
}

const calculateAwardPerPerson = (totalPot: number, count: number): string => {
  if (totalPot > 0 && count > 0) {
    return Math.floor(totalPot * 0.01).toLocaleString();
  }
  return "0";
};

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const PotDisplay = ({ totalPot }: { totalPot: number }) => (
  <motion.div
    className="text-center w-full"
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: 0.2, duration: CONTAINER_ANIMATION_DURATION }}
  >
    <div className="flex items-center justify-center gap-2 text-amber-200 uppercase text-xs font-black mb-1">
      <span>POT</span>
    </div>
    <div className="flex items-center justify-center gap-2 w-full">
      <motion.p
        className="text-5xl leading-none font-black text-yellow-300 drop-shadow-[0_0_18px_rgba(253,224,71,0.65)]"
        key={totalPot}
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 0.3 }}
      >
        {totalPot.toLocaleString()}
      </motion.p>
      <EspontIcon className="h-8 w-8 drop-shadow-[0_0_8px_rgba(253,224,71,0.75)]" />
    </div>
  </motion.div>
);

const TimerDisplay = ({ timeRemaining }: { timeRemaining: number }) => (
  <motion.div
    className="flex items-center gap-2 rounded-full border border-cyan-300/50 bg-cyan-950/55 px-4 py-1.5 text-xl font-black text-cyan-200 shadow-[0_0_22px_rgba(103,232,249,0.28)]"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 0.3, duration: CONTAINER_ANIMATION_DURATION }}
  >
    <Timer className="w-5 h-5" />
    <span>{formatTime(timeRemaining)}</span>
  </motion.div>
);

const StatsDisplay = ({
  totalPot,
  participantCount,
}: {
  totalPot: number;
  participantCount: number;
}) => (
  <motion.div
    className="grid grid-cols-2 gap-3 w-full"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.4, duration: CONTAINER_ANIMATION_DURATION }}
  >
    <div className="flex min-h-[44px] items-center justify-between gap-3 rounded-lg border border-emerald-300/45 bg-emerald-950/55 px-3 py-2 shadow-[0_0_18px_rgba(52,211,153,0.14)]">
      <div className="flex items-center gap-1.5 text-xs font-black uppercase text-emerald-200">
        <Users className="w-4 h-4" />
        <span>Users</span>
      </div>
      <motion.p
        className="text-2xl leading-none font-black text-emerald-300"
        key={participantCount}
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.8 }}
      >
        {participantCount}
      </motion.p>
    </div>
    <div className="flex min-h-[44px] items-center justify-between gap-3 rounded-lg border border-sky-300/45 bg-sky-950/55 px-3 py-2 shadow-[0_0_18px_rgba(56,189,248,0.14)]">
      <div className="flex items-center gap-1.5 text-xs font-black uppercase text-sky-200">
        <Gift className="w-4 h-4" />
        <span>Each</span>
      </div>
      <div className="flex items-center gap-1.5 text-sky-300">
        <motion.p
          className="text-2xl leading-none font-black"
          key={calculateAwardPerPerson(totalPot, participantCount)}
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          {calculateAwardPerPerson(totalPot, participantCount)}
        </motion.p>
        <EspontIcon className="h-5 w-5 drop-shadow-[0_0_8px_rgba(253,224,71,0.75)]" />
      </div>
    </div>
  </motion.div>
);

const JoinHint = ({ maxPerUser }: { maxPerUser: number | null }) => (
  <motion.div
    className="flex flex-col items-center gap-1.5 rounded-xl border border-yellow-300/45 bg-yellow-950/45 px-4 py-2.5 text-center w-full shadow-[0_0_20px_rgba(253,224,71,0.18)]"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.35, duration: CONTAINER_ANIMATION_DURATION }}
  >
    <span className="flex items-center justify-center gap-1 text-[11px] font-black uppercase tracking-wide text-yellow-200">
      How to join?
      {maxPerUser != null && maxPerUser > 0 && (
        <span className="flex items-center gap-1 normal-case text-yellow-100/70">
          - max {maxPerUser.toLocaleString()}
          <EspontIcon className="h-3 w-3" />
        </span>
      )}
    </span>
    <div className="flex items-center justify-center gap-2">
      <code className="rounded-md bg-black/45 px-3 py-1 text-lg font-black text-yellow-300 drop-shadow-[0_0_10px_rgba(253,224,71,0.5)]">
        !rain [espont]
      </code>
    </div>
  </motion.div>
);

interface Contributor {
  twitch_id: string;
  twitch_name: string;
  amount: number;
  timestamp: string;
}

const ContributorsList = ({
  contributors,
}: {
  contributors: Contributor[];
}) => {
  if (contributors.length === 0) return null;

  return (
    <motion.div
      className="mt-1 w-full overflow-hidden"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: CONTAINER_ANIMATION_DURATION }}
    >
      <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase text-purple-200">
        <UserRound className="w-4 h-4" />
        <span>Contributors</span>
      </div>
      <div className="space-y-1 overflow-hidden">
        {contributors.slice(0, 5).map((contributor, index) => (
          <motion.div
            key={`${contributor.twitch_id}-${contributor.timestamp}`}
            className="flex h-[34px] items-center justify-between gap-3 rounded-md border border-purple-300/35 bg-purple-950/60 px-3 text-white shadow-[0_0_16px_rgba(168,85,247,0.12)]"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 * index }}
          >
            <span className="min-w-0 truncate text-base font-black">
              {contributor.twitch_name}
            </span>
            <span className="flex-shrink-0 text-sm font-black text-yellow-300">
              +{contributor.amount.toLocaleString()}
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

const EndBanner = ({
  totalPot,
  participantCount,
}: {
  totalPot: number;
  participantCount: number;
}) => (
  <motion.div
    className="mt-2 text-center p-2 rounded-xl bg-gradient-to-r from-yellow-900/50 to-green-900/50 border-2 border-yellow-400 w-full"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.6, duration: CONTAINER_ANIMATION_DURATION }}
  >
    <h3 className="text-lg font-black text-yellow-300">VÉGE</h3>
    <p className="text-white text-xs mt-1">
      Összesen: {totalPot.toLocaleString()} {CURRENCY_UNIT}
    </p>
    <p className="text-green-300 text-sm font-black">
      1 user: {calculateAwardPerPerson(totalPot, participantCount)}{" "}
      {CURRENCY_UNIT}
    </p>
  </motion.div>
);

export function RainOverlay({
  enabled = true,
  onEventReceived,
  animationSpeed = 1,
  autoFadeOut = true,
  fadeOutDelay = 5000,
}: RainOverlayProps) {
  const { state } = useRainWebSocket();
  const [shouldDisplay, setShouldDisplay] = useState(false);
  const [fadeOutTimer, setFadeOutTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [endSequenceActive, setEndSequenceActive] = useState(false);
  const [frozenDrops, setFrozenDrops] = useState<FrozenDrop[]>([]);
  const rainDropsRef = useRef<RainDropsHandle>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const lastContributorCountRef = useRef(0);

  const getAudioContext = useCallback(async () => {
    const AudioContextClass =
      globalThis.AudioContext || (globalThis as any).webkitAudioContext;
    if (!AudioContextClass) return null;

    const context = audioContextRef.current || new AudioContextClass();
    audioContextRef.current = context;

    try {
      await context.resume();
    } catch {
      return null;
    }

    return context;
  }, []);

  const playCoinSound = useCallback(async () => {
    const context = await getAudioContext();
    if (!context) return;

    const now = context.currentTime;
    const masterGain = context.createGain();
    masterGain.gain.setValueAtTime(0.0001, now);
    masterGain.gain.exponentialRampToValueAtTime(0.055, now + 0.015);
    masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
    masterGain.connect(context.destination);

    [1320, 1760, 2340].forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = now + index * 0.045;

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, start);
      oscillator.frequency.exponentialRampToValueAtTime(
        frequency * 0.84,
        start + 0.18,
      );
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.45 / (index + 1), start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.22);

      oscillator.connect(gain);
      gain.connect(masterGain);
      oscillator.start(start);
      oscillator.stop(start + 0.24);
    });
  }, [getAudioContext]);

  useEffect(() => {
    if (onEventReceived) {
      onEventReceived(state);
    }
  }, [state, onEventReceived]);

  useEffect(() => {
    if (!enabled) {
      setShouldDisplay(false);
      return;
    }

    if (state.isActive) {
      setShouldDisplay(true);
      setEndSequenceActive(false);
      if (fadeOutTimer) {
        clearTimeout(fadeOutTimer);
        setFadeOutTimer(null);
      }
    } else if (state.status === "closed" && !endSequenceActive) {
      const drops = rainDropsRef.current?.getPositions() ?? [];
      setFrozenDrops(drops);
      setEndSequenceActive(true);
    }

    return () => {
      if (fadeOutTimer) {
        clearTimeout(fadeOutTimer);
      }
    };
  }, [
    state.isActive,
    state.status,
    enabled,
    autoFadeOut,
    fadeOutDelay,
    endSequenceActive,
    fadeOutTimer,
  ]);

  useEffect(() => {
    if (!state.isActive) {
      lastContributorCountRef.current = state.contributors.length;
      return;
    }

    if (state.contributors.length > lastContributorCountRef.current) {
      playCoinSound();
    }

    lastContributorCountRef.current = state.contributors.length;
  }, [state.contributors.length, state.isActive, playCoinSound]);

  useEffect(() => {
    return () => {
      audioContextRef.current?.close().catch(() => undefined);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  const bgGradient =
    "linear-gradient(135deg, rgba(20, 10, 40, 0.95) 0%, rgba(40, 20, 60, 0.95) 50%, rgba(20, 10, 40, 0.95) 100%)";
  const overlayBlur =
    "radial-gradient(circle, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 100%)";
  const isEndPhase = endSequenceActive && state.finalStats;
  const showActiveContent = !endSequenceActive;

  return (
    <AnimatePresence>
      {shouldDisplay && (
        <motion.div
          className="absolute inset-0 flex flex-col p-4 z-50 rounded-3xl"
          initial={{ opacity: 0, scale: 0.95, x: 0 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          exit={{
            opacity: 0,
            x: "115%",
            scale: 0.98,
            rotate: 2,
            filter: "blur(8px)",
            transition: {
              duration: EXIT_ANIMATION_DURATION,
              ease: [0.76, 0, 0.24, 1],
            },
          }}
          transition={{
            duration: CONTAINER_ANIMATION_DURATION,
          }}
          // Nincs `backdropFilter`: a gradiens 95%-ban opak, tehat a mogotte
          // levo kep ugyis alig latszik at -- a blur szemre semmit nem adott,
          // cserebe a bongeszo MINDEN kepkockan ujraszurte a panel teljes
          // hatteret. Elo adasban ez az egyik legdragabb muvelet.
          style={{ background: bgGradient }}
        >
          {showActiveContent && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl z-0">
              <RainDrops
                ref={rainDropsRef}
                isActive={state.isActive}
                animationSpeed={animationSpeed}
              />
            </div>
          )}

          {showActiveContent && (
            // A sotetito gradiens marad, a `backdropFilter: blur(2px)` viszont
            // elment. Az a reteg PONT az eso folott ult, tehat a hattere minden
            // kepkockan valtozott, es a bongeszo minden kepkockan ujra elmosta
            // a teljes panelt. Ez volt a lag fo forrasa.
            <div
              className="absolute inset-0 pointer-events-none rounded-3xl z-5"
              style={{ background: overlayBlur }}
            />
          )}

          {isEndPhase && (
            <RainEndSequence
              finalStats={state.finalStats!}
              contributors={state.contributors}
              frozenDrops={frozenDrops}
              topContributors={state.topContributors}
              onDone={() => setShouldDisplay(false)}
            />
          )}

          {showActiveContent && (
            <div className="absolute inset-0 flex flex-col items-center justify-start pointer-events-auto z-10 gap-3 px-5 pb-5 pt-8">
              <PotDisplay totalPot={state.totalPot} />
              <TimerDisplay timeRemaining={state.timeRemaining} />
              <JoinHint maxPerUser={state.maxPerUser} />
              <StatsDisplay
                totalPot={state.totalPot}
                participantCount={state.participantCount}
              />
              <ContributorsList contributors={state.contributors} />
              {state.status === "closed" && state.finalStats && (
                <EndBanner
                  totalPot={state.finalStats.totalPot}
                  participantCount={state.participantCount}
                />
              )}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
